import React, { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";

const STORAGE_KEY = "arabell-bg-audio-muted";
const VOLUME = 0.22;

/**
 * Background music with aggressive autoplay strategy:
 * 1. Start muted autoplay immediately (allowed by all browsers).
 * 2. Try to unmute right away (works on returning visits / Chrome MEI trusted sites).
 * 3. If browser blocks unmuted audio, listen for ANY user interaction
 *    (mousemove/click/touch/scroll/keydown) and unmute at the first gesture.
 * 4. User can toggle via the floating button; state persists in localStorage.
 */
const BackgroundAudio = () => {
  const audioRef = useRef(null);
  const [userMuted, setUserMuted] = useState(() => {
    const stored = typeof window !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;
    return stored === "true";
  });
  const [isPlayingAudible, setIsPlayingAudible] = useState(false);

  // Init + muted autoplay immediately on mount
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    a.volume = VOLUME;
    a.loop = true;
    a.muted = true; // muted autoplay is always allowed
    a.play().catch(() => {});
  }, []);

  // Try to unmute whenever userMuted changes to false
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;

    if (userMuted) {
      a.muted = true;
      a.pause();
      setIsPlayingAudible(false);
      localStorage.setItem(STORAGE_KEY, "true");
      return;
    }

    localStorage.setItem(STORAGE_KEY, "false");

    const tryUnmute = async () => {
      try {
        a.muted = false;
        await a.play();
        setIsPlayingAudible(true);
      } catch {
        // Browser blocked -> wait for first user gesture
        a.muted = true;
        a.play().catch(() => {});
        const onGesture = async () => {
          try {
            a.muted = false;
            await a.play();
            setIsPlayingAudible(true);
          } catch {
            /* ignore */
          }
        };
        const events = ["pointerdown", "click", "touchstart", "keydown", "scroll", "wheel", "mousemove"];
        events.forEach((ev) =>
          window.addEventListener(ev, onGesture, { once: true, passive: true })
        );
      }
    };

    tryUnmute();
  }, [userMuted]);

  const toggle = () => setUserMuted((m) => !m);

  return (
    <>
      <audio
        ref={audioRef}
        src="/audio/arabell-bg.mp3"
        preload="auto"
        autoPlay
        loop
        muted
        playsInline
        data-testid="background-audio"
      />
      <button
        type="button"
        onClick={toggle}
        aria-label={userMuted ? "Riattiva musica" : "Disattiva musica"}
        data-testid="audio-toggle-btn"
        className="no-print fixed bottom-6 left-6 z-[90] h-12 w-12 rounded-full bg-black/60 backdrop-blur-md border border-arabell-gold/40 hover:border-arabell-gold hover:bg-black/80 text-arabell-gold transition-all duration-300 flex items-center justify-center shadow-lg group"
      >
        {userMuted ? (
          <VolumeX className="w-5 h-5" strokeWidth={1.5} />
        ) : (
          <Volume2 className={`w-5 h-5 ${isPlayingAudible ? "animate-pulse" : ""}`} strokeWidth={1.5} />
        )}
        <span className="absolute left-full ml-3 px-3 py-1 bg-black/80 text-white/80 text-[10px] tracking-[0.3em] uppercase whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none border border-arabell-gold/20">
          {userMuted ? "Music off" : isPlayingAudible ? "Music on" : "Tap to play"}
        </span>
      </button>
    </>
  );
};

export default BackgroundAudio;
